import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import DatePicker from "../Common/DatePicker";
import {
  employeeService,
  branchService,
  departmentService,
  designationService,
  gradesService,
  categoryService,
  subDepartmentService,
} from "../../services/api";


export default function Filters({
  onApply,
  onClose,
  onClear,
  darkMode = false,
  initialFilters = {},
  payslipMonthYear,
}) {
  // Filter state (default to 'all' for dateFilterType so date inputs start disabled)
  const [filters, setFilters] = useState({
    dateFilterType: "all",
    ...initialFilters,
  });
  // Guard to avoid duplicate loads in React 18 Strict Mode (development)
  const loadedRef = useRef(false);


  // Master data states
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);


  // UI states
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [loading, setLoading] = useState(false);


  // Load master data on component mount (once)
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadMasterData();
  }, []);


  const loadMasterData = async () => {
    try {
      setLoading(true);
      const [
        branchesRes,
        departmentsRes,
        designationsRes,
        gradesRes,
        categoriesRes,
        subDepartmentsRes,
        reportingManagersRes,
      ] = await Promise.all([
        branchService.getBranches(),
        departmentService.getDepartments(),
        designationService.getDesignations(),
        gradesService.getGrades(),
        categoryService.getAllCategories(),
        subDepartmentService.getAllSubDepartments(),
        employeeService.getReportingEmployees(),
      ]);


      setBranches(branchesRes.data || []);
      setDepartments(departmentsRes.data || []);
      setDesignations(designationsRes.data || []);
      setGrades(gradesRes.data || []);
      setCategories(categoriesRes.data || []);
      setSubDepartments(subDepartmentsRes.data || []);
      setReportingManagers(
        reportingManagersRes.data || reportingManagersRes || []
      );
    } catch (error) {
      console.error("Error loading master data:", error);
    } finally {
      setLoading(false);
    }
  };


  // Validation functions from OrgDetailsForm
  const validateDateRange = (from, to) => {
    if (!from && !to) return null;


    if (from && to) {
      const fromD = new Date(from);
      const toD = new Date(to);


      if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) {
        return "Invalid date format";
      }


      if (fromD && fromD > toD) {
        return "From date cannot be greater than To date";
      } else if (toD && fromD > toD) {
        return "To date cannot be less than From date";
      }
    }


    return null;
  };


  const validateCTCRange = (ctcFrom, ctcTo) => {
    if (!ctcFrom && !ctcTo) return null;


    // Check for negative values
    if (
      (ctcFrom && parseFloat(ctcFrom) < 0) ||
      (ctcTo && parseFloat(ctcTo) < 0)
    ) {
      return "CTC cannot be negative";
    }


    // Check if both values are provided and validate range
    if (ctcFrom && ctcTo) {
      const from = parseFloat(ctcFrom);
      const to = parseFloat(ctcTo);


      if (isNaN(from) || isNaN(to)) {
        return "CTC values must be valid numbers";
      }


      if (from > to) {
        return "CTC From must be less than or equal to CTC To";
      }
    }


    // Check individual values
    if (ctcFrom && parseFloat(ctcFrom) > 999999999) {
      return "CTC From cannot exceed 999,999,999";
    }


    if (ctcTo && parseFloat(ctcTo) > 999999999) {
      return "CTC To cannot exceed 999,999,999";
    }


    return null;
  };


  const validateNumericId = (value, fieldName) => {
    if (!value) return null;
    if (isNaN(value) || value < 0) {
      return `${fieldName} must be a valid positive number`;
    }
    return null;
  };


  // Comprehensive validation function
  const validateFilters = (filtersObj) => {
    const errors = {};


    // Date range validation
    if (filtersObj.dateFilterType && filtersObj.dateFilterType !== "all") {
      const dateError = validateDateRange(
        filtersObj.reportDateFrom,
        filtersObj.reportDateTo
      );
      if (dateError) {
        errors.dateError = dateError;
      }
    }


    // CTC range validation
    const ctcError = validateCTCRange(filtersObj.ctcFrom, filtersObj.ctcTo);
    if (ctcError) {
      errors.ctcError = ctcError;
    }


    // Numeric ID validations
    if (filtersObj.branch) {
      const branchError = validateNumericId(filtersObj.branch, "Branch");
      if (branchError) errors.branch = branchError;
    }


    if (filtersObj.department) {
      const deptError = validateNumericId(filtersObj.department, "Department");
      if (deptError) errors.department = deptError;
    }


    if (filtersObj.designation) {
      const designationError = validateNumericId(
        filtersObj.designation,
        "Designation"
      );
      if (designationError) errors.designation = designationError;
    }


    if (filtersObj.grade) {
      const gradeError = validateNumericId(filtersObj.grade, "Grade");
      if (gradeError) errors.grade = gradeError;
    }


    if (filtersObj.category) {
      const categoryError = validateNumericId(filtersObj.category, "Category");
      if (categoryError) errors.category = categoryError;
    }


    if (filtersObj.subDepartment) {
      const subDeptError = validateNumericId(
        filtersObj.subDepartment,
        "Sub Department"
      );
      if (subDeptError) errors.subDepartment = subDeptError;
    }


    return errors;
  };


  const handleChange = (key) => (e) => {
    let value = e.target.value;


    // Special handling for CTC fields
    if (key === "ctcFrom" || key === "ctcTo") {
      if (value === "" || !isNaN(value)) {
        const nextFilters = { ...filters, [key]: value };
        setFilters(nextFilters);
        // Validate on change if field has been touched
        if (touchedFields[key]) {
          const errors = validateFilters(nextFilters);
          setFormErrors(errors);
        }
        return;
      }
    }


    if (typeof value === "string") value = value.trim();
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    // Validate on change if field has been touched
    if (touchedFields[key]) {
      const errors = validateFilters(nextFilters);
      setFormErrors(errors);
    }


    // Special handling for date fields - validate date range when either changes
    if (
      (key === "reportDateFrom" ||
        key === "reportDateTo" ||
        key === "dateFilterType") &&
      (touchedFields.reportDateFrom || touchedFields.reportDateTo)
    ) {
      const errors = validateFilters(nextFilters);
      setFormErrors(errors);
    }
  };


  // Handle field blur
  const handleFieldBlur = (fieldName) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));


    const errors = validateFilters(filters);
    setFormErrors(errors);
  };


  // Helper to transform filter object into API params
  const buildApiParamsFromFilters = (filtersObj) => {
    const params = {};
    if (!filtersObj) return params;


    // Map of filter keys to API param keys and their type
    const mappings = [
      ["branch", "branchId", "number"],
      ["department", "departmentId", "number"],
      ["designation", "designationId", "number"],
      ["grade", "gradeId", "number"],
      ["category", "categoryId", "number"],
      ["subDepartment", "subDepartmentId", "number"],
      ["employmentType", "employmentType", "string"],
      ["status", "employmentStatus", "string"],
      ["pfApplicable", "pfApplicable", "boolean"],
      ["ceilingPf", "ceilingPf", "boolean"],
      ["esiApplicable", "esiApplicable", "boolean"],
      ["ptApplicable", "ptApplicable", "boolean"],
      ["gratuityApplicable", "gratuityApplicable", "boolean"],
      ["epsApplicable", "epsApplicable", "boolean"],
      ["hasUAN", "hasUAN", "boolean"],
      ["hasESIIP", "hasESIIP", "boolean"],
      ["ctcFrom", "ctcFrom", "number"],
      ["ctcTo", "ctcTo", "number"],
      ["dateFilterType", "dateFilterType", "string"],
      ["reportingManagerId", "reportingManagerId", "number"],
      ["gender", "gender", "string"],
      ["userStatus", "userStatus", "string"],
      ["bloodGroup", "bloodGroup", "string"],
      ["search", "search", "string"],
    ];


    mappings.forEach(([key, apiKey, type]) => {
      let val = filtersObj[key];
      if (val === undefined || val === null || val === "") return;
      if (type === "boolean") {
        if (val === true || val === false) params[apiKey] = val;
        else if (val === "true" || val === "false")
          params[apiKey] = val === "true";
      } else if (type === "number") {
        const n = Number(val);
        if (!Number.isNaN(n)) params[apiKey] = n;
      } else {
        const normalized = typeof val === "string" ? val.trim() : val;
        // Keep parity with Employee filter behavior:
        // "all" is a default/non-filter value and should not be sent.
        if (normalized === "" || normalized === "all") return;
        params[apiKey] = normalized;
      }
    });


    // Date range only when a specific date type is selected
    const dType = filtersObj.dateFilterType;
    const from = filtersObj.reportDateFrom;
    const to = filtersObj.reportDateTo;
    if (dType && dType !== "all") {
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;
    }


    return params;
  };


  const handleClear = () => {
    setFilters({
      // date filter
      dateFilterType: "all",
      reportDateFrom: "",
      reportDateTo: "",


      // master data
      branch: "",
      department: "",
      designation: "",
      grade: "",
      category: "",
      subDepartment: "",


      // employment details
      employmentType: "",
      status: "",
      reportingManagerId: "",
      gender: "",
      userStatus: "",
      bloodGroup: "",
      search: "",


      // statutory applicability
      pfApplicable: "",
      esiApplicable: "",
      ptApplicable: "",
      gratuityApplicable: "",
      epsApplicable: "",
      ceilingPf: "",
      hasUAN: "",
      hasESIIP: "",


      // CTC
      ctcFrom: "",
      ctcTo: "",
    });


    setFormErrors({});
    setTouchedFields({});


    if (typeof onClear === "function") {
      onClear();
    }
  };


  const handleApply = async () => {
    // Mark all fields as touched to show all errors
    const allFields = [
      "branch",
      "department",
      "designation",
      "grade",
      "category",
      "subDepartment",
      "reportDateFrom",
      "reportDateTo",
      "ctcFrom",
      "ctcTo",
      "dateFilterType",
      "EmploymentType",
      "EmploymentStatus",
      "Gender",
      "UserStatus",
      "BloodGroup",
      "Search",
      "ReportingManagerId",
      "pfApplicable",
      "ceilingPf",
      "esiApplicable",
      "ptApplicable",
      "gratuityApplicable",
      "epsApplicable",
      "hasUAN",
      "hasESIIP",
    ];
    const newTouchedFields = {};
    allFields.forEach((field) => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);


    // Validate all filters
    const errors = validateFilters(filters);
    setFormErrors(errors);


    // Check if there are any errors
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      console.log("Validation errors:", errors);
      return;
    }


    try {
      const params = buildApiParamsFromFilters(filters);
      if (payslipMonthYear && typeof payslipMonthYear === "string" && payslipMonthYear.trim()) {
        params.payslipMonthYear = payslipMonthYear.trim();
      }
      const resp = await employeeService.getEmployees(params);


      let employeesData = [];
      if (!resp) employeesData = [];
      else if (Array.isArray(resp)) employeesData = resp;
      else if (resp.data && Array.isArray(resp.data)) employeesData = resp.data;
      else if (resp.employees && Array.isArray(resp.employees))
        employeesData = resp.employees;
      else if (resp.success && Array.isArray(resp.data))
        employeesData = resp.data;
      else employeesData = [];


      if (typeof onApply === "function") {
        onApply(employeesData, filters, params);
      }
    } catch (err) {
      console.error("Filters: failed to fetch employees with filters", err);
      if (typeof onApply === "function") {
        onApply([], filters, {});
      }
    }
  };


  // Focus management
  const modalRef = React.useRef(null);
  React.useEffect(() => {
    const el = modalRef.current;
    if (el) {
      const focusable = el.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const currentlyFocused = document.activeElement;


      if (focusable && !el.contains(currentlyFocused)) {
        focusable.focus();
      }
    }


    const onKey = (e) => {
      if (e.key === "Escape" && !e.target.matches("input, textarea, select")) {
        if (typeof onClose === "function") onClose();
      }
    };


    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);


  // Check if form has errors
  const hasErrors = Object.keys(formErrors).length > 0;
  const isFieldSelected = (fieldName, value) => {
    if (value === undefined || value === null) return false;
    const v = String(value);
    if (v === "") return false;
    if (fieldName === "dateFilterType") {
      return v !== "all";
    }
    return true;
  };

  const getFieldBorderClass = (fieldName, value) => {
    if (formErrors[fieldName]) return "border-red-500";
    if (isFieldSelected(fieldName, value)) {
      return "border-green-500";
    }
    return "";
  };

  const getFieldTextClass = (fieldName, value) => {
    if (!isFieldSelected(fieldName, value)) return "";
    return darkMode ? "text-green-300" : "text-green-700";
  };

  const getLabelTextClass = (fieldName, value) => {
    if (formErrors[fieldName]) return "text-red-500";
    if (isFieldSelected(fieldName, value)) {
      return darkMode ? "text-green-300" : "text-green-700";
    }
    return darkMode ? "text-gray-400" : "text-gray-500";
  };

  const findNameById = (list, id) => {
    const arr = Array.isArray(list) ? list : [];
    const item = arr.find((x) => String(x?.id) === String(id));
    return item?.name || "";
  };

  const getSelectedSummary = () => {
    const items = [];
    if (isFieldSelected("branch", filters.branch)) {
      items.push({
        label: "Branch",
        value: findNameById(branches, filters.branch),
      });
    }
    if (isFieldSelected("department", filters.department)) {
      items.push({
        label: "Department",
        value: findNameById(departments, filters.department),
      });
    }
    if (isFieldSelected("designation", filters.designation)) {
      items.push({
        label: "Designation",
        value: findNameById(designations, filters.designation),
      });
    }
    if (isFieldSelected("grade", filters.grade)) {
      items.push({ label: "Grade", value: findNameById(grades, filters.grade) });
    }
    if (isFieldSelected("category", filters.category)) {
      items.push({
        label: "Category",
        value: findNameById(categories, filters.category),
      });
    }
    if (isFieldSelected("subDepartment", filters.subDepartment)) {
      items.push({
        label: "Sub Department",
        value: findNameById(subDepartments, filters.subDepartment),
      });
    }
    if (isFieldSelected("bloodGroup", filters.bloodGroup)) {
      items.push({ label: "Blood Group", value: String(filters.bloodGroup) });
    }
    if (isFieldSelected("userStatus", filters.userStatus)) {
      items.push({ label: "User Status", value: String(filters.userStatus) });
    }
    if (isFieldSelected("gender", filters.gender)) {
      items.push({ label: "Gender", value: String(filters.gender) });
    }
    if (isFieldSelected("reportingManagerId", filters.reportingManagerId)) {
      items.push({
        label: "Reporting Manager",
        value: String(filters.reportingManagerId),
      });
    }
    if (isFieldSelected("employmentType", filters.employmentType)) {
      items.push({ label: "Employment Type", value: String(filters.employmentType) });
    }
    if (isFieldSelected("status", filters.status)) {
      items.push({ label: "Status", value: String(filters.status) });
    }
    if (isFieldSelected("dateFilterType", filters.dateFilterType)) {
      items.push({ label: "Date Filter", value: String(filters.dateFilterType) });
    }
    if (isFieldSelected("pfApplicable", filters.pfApplicable)) {
      items.push({ label: "PF", value: String(filters.pfApplicable) });
    }
    if (isFieldSelected("epsApplicable", filters.epsApplicable)) {
      items.push({ label: "EPS", value: String(filters.epsApplicable) });
    }
    if (isFieldSelected("esiApplicable", filters.esiApplicable)) {
      items.push({ label: "ESI", value: String(filters.esiApplicable) });
    }
    if (isFieldSelected("search", filters.search)) {
      items.push({ label: "Search", value: "…" });
    }
    return items.filter((x) => x.value);
  };


  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Report Filters"
    >
      <div
        ref={modalRef}
        className={`w-[95%] max-w-6xl h-[80vh] rounded-none shadow-xl flex flex-col overflow-hidden ${
          darkMode ? "bg-[#2C2C2C]" : "bg-white"
        }`}
      >
        {/* Fixed Header */}
        <div
          className={`flex justify-between items-center p-6 border-b flex-shrink-0 ${
            darkMode ? "border-[#3C3C3C]" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-xl font-semibold ${
              darkMode ? "text-[#E0E0E0]" : "text-gray-900"
            }`}
          >
            Filter
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-none hover:bg-gray-100 ${
              darkMode ? "text-[#A6A9C8] hover:bg-[#3C3C3C]" : "text-gray-500"
            }`}
            aria-label="Close filters"
          >
            <X size={20} />
          </button>
        </div>


        {/* Scrollable Content */}
        <fieldset
          disabled={loading}
          className="flex-1 overflow-y-auto overflow-x-hidden p-6"
        >
          {/* Master Data */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Master Data
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative">
                <select
                  value={filters.branch || ""}
                  onChange={handleChange("branch")}
                  onBlur={() => handleFieldBlur("branch")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("branch", filters.branch)} ${getFieldTextClass("branch", filters.branch)}`}
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("branch", filters.branch)}`}
                >
                  Branch
                </label>
                {formErrors.branch && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.branch}
                  </div>
                )}
              </div>


              <div className="relative">
                <select
                  value={filters.department || ""}
                  onChange={handleChange("department")}
                  onBlur={() => handleFieldBlur("department")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("department", filters.department)} ${getFieldTextClass("department", filters.department)}`}
                >
                  <option value="">All Departments</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("department", filters.department)}`}
                >
                  Department
                </label>
                {formErrors.department && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.department}
                  </div>
                )}
              </div>


              <div className="relative">
                <select
                  value={filters.designation || ""}
                  onChange={handleChange("designation")}
                  onBlur={() => handleFieldBlur("designation")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("designation", filters.designation)} ${getFieldTextClass("designation", filters.designation)}`}
                >
                  <option value="">All Designations</option>
                  {designations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("designation", filters.designation)}`}
                >
                  Designation
                </label>
                {formErrors.designation && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.designation}
                  </div>
                )}
              </div>


              {/* Additional master filters: Grade, Category, Sub-department */}
              <div className="relative">
                <select
                  value={filters.grade || ""}
                  onChange={handleChange("grade")}
                  onBlur={() => handleFieldBlur("grade")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("grade", filters.grade)} ${getFieldTextClass("grade", filters.grade)}`}
                >
                  <option value="">All Grades</option>
                  {grades.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("grade", filters.grade)}`}
                >
                  Grade
                </label>
                {formErrors.grade && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.grade}
                  </div>
                )}
              </div>


              <div className="relative">
                <select
                  value={filters.category || ""}
                  onChange={handleChange("category")}
                  onBlur={() => handleFieldBlur("category")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("category", filters.category)} ${getFieldTextClass("category", filters.category)}`}
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("category", filters.category)}`}
                >
                  Category
                </label>
                {formErrors.category && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.category}
                  </div>
                )}
              </div>


              <div className="relative">
                <select
                  value={filters.subDepartment || ""}
                  onChange={handleChange("subDepartment")}
                  onBlur={() => handleFieldBlur("subDepartment")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("subDepartment", filters.subDepartment)} ${getFieldTextClass("subDepartment", filters.subDepartment)}`}
                >
                  <option value="">All Sub Departments</option>
                  {subDepartments.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("subDepartment", filters.subDepartment)}`}
                >
                  Sub Department
                </label>
                {formErrors.subDepartment && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.subDepartment}
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Employment Details */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Employment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative">
                <select
                  value={filters.employmentType}
                  onChange={handleChange("employmentType")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("employmentType", filters.employmentType)} ${getFieldTextClass("employmentType", filters.employmentType)}`}
                >
                  <option value="">All Types</option>
                  <option value="full-time">Full Time</option>
                  {/* <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="intern">Intern</option>
                  <option value="consultant">Consultant</option> */}
                  <option value="external">External</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("employmentType", filters.employmentType)}`}>
                  Employment Type
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.status}
                  onChange={handleChange("status")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("status", filters.status)} ${getFieldTextClass("status", filters.status)}`}
                >
                  <option value="">All Status</option>
                  <option value="probation">Probation</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="resigned">Resigned</option>
                  <option value="relieved">Relieved</option>
                  <option value="terminated">Terminated</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("status", filters.status)}`}>
                  Employment Status
                </label>
              </div>
            </div>
          </div>


          {/* Search & User Filter */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Search & User Filter
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative">
                <input
                  type="text"
                  name="employee_filter_query"
                  id="admin-filters-search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  data-lpignore="true"
                  value={filters.search || ""}
                  onChange={handleChange("search")}
                  className={`block px-3 py-2 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("search", filters.search)} ${getFieldTextClass("search", filters.search)}`}
                  placeholder="Search by name, phone, employee code, PAN, or Aadhaar"
                />
              </div>


              <div className="relative">
                <select
                  value={filters.gender || ""}
                  onChange={handleChange("gender")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("gender", filters.gender)} ${getFieldTextClass("gender", filters.gender)}`}
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("gender", filters.gender)}`}
                >
                  Gender
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.userStatus || ""}
                  onChange={handleChange("userStatus")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("userStatus", filters.userStatus)} ${getFieldTextClass("userStatus", filters.userStatus)}`}
                >
                  <option value="">All User Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("userStatus", filters.userStatus)}`}
                >
                  User Status
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.bloodGroup || ""}
                  onChange={handleChange("bloodGroup")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("bloodGroup", filters.bloodGroup)} ${getFieldTextClass("bloodGroup", filters.bloodGroup)}`}
                >
                  <option value="">All Blood Groups</option>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                    (bg) => (
                      <option key={bg} value={bg}>
                        {bg}
                      </option>
                    )
                  )}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("bloodGroup", filters.bloodGroup)}`}
                >
                  Blood Group
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.reportingManagerId || ""}
                  onChange={handleChange("reportingManagerId")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("reportingManagerId", filters.reportingManagerId)} ${getFieldTextClass("reportingManagerId", filters.reportingManagerId)}`}
                >
                  <option value="">All Reporting Managers</option>
                  {(reportingManagers?.data || reportingManagers).map((rm) => (
                    <option key={rm.id} value={rm.id}>
                      {rm.empCode} - {rm.user?.name || rm.user?.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("reportingManagerId", filters.reportingManagerId)}`}
                >
                  Reporting Manager
                </label>
              </div>
            </div>
          </div>


          {/* Date Filter & Statutory */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Date Filter
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="relative">
                <select
                  value={filters.dateFilterType}
                  onChange={handleChange("dateFilterType")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("dateFilterType", filters.dateFilterType)} ${getFieldTextClass("dateFilterType", filters.dateFilterType)}`}
                >
                  <option value="all">All</option>
                  <option value="dateOfJoin">Date of Joining</option>
                  <option value="dateOfRelieving">Date of Relieving</option>
                  <option value="confirmationDate">Confirmation Date</option>
                  <option value="resignationDate">Resignation Date</option>
                  <option value="terminationDate">Termination Date</option>
                  <option value="dateOfBirth">Date of Birth</option>
                  <option value="extendedProbationDate">
                    Extended Probation Date
                  </option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("dateFilterType", filters.dateFilterType)}`}>
                  Date Filter Type
                </label>
              </div>


              <DatePicker
                value={filters.reportDateFrom || ""}
                onChange={handleChange("reportDateFrom")}
                onBlur={() => handleFieldBlur("reportDateFrom")}
                disabled={filters.dateFilterType === "all"}
                darkMode={darkMode}
                label="From Date"
                max={filters.reportDateTo || ""}
                maxErrorText="From Date cannot be greater than To Date"
                error={formErrors.dateError}
              />


              <DatePicker
                value={filters.reportDateTo || ""}
                onChange={handleChange("reportDateTo")}
                onBlur={() => handleFieldBlur("reportDateTo")}
                disabled={filters.dateFilterType === "all"}
                darkMode={darkMode}
                label="To Date"
                min={filters.reportDateFrom}
                minErrorText="To Date cannot be less than From Date"
                error={formErrors.dateError}
              />
            </div>


            {formErrors.dateError && (
              <div className="text-sm text-red-500 mt-2">
                {formErrors.dateError}
              </div>
            )}


            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Statutory Applicability
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative">
                <select
                  value={filters.pfApplicable}
                  onChange={handleChange("pfApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("pfApplicable", filters.pfApplicable)} ${getFieldTextClass("pfApplicable", filters.pfApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("pfApplicable", filters.pfApplicable)}`}>
                  PF Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.epsApplicable}
                  onChange={handleChange("epsApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("epsApplicable", filters.epsApplicable)} ${getFieldTextClass("epsApplicable", filters.epsApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("epsApplicable", filters.epsApplicable)}`}>
                  EPS Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.esiApplicable}
                  onChange={handleChange("esiApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("esiApplicable", filters.esiApplicable)} ${getFieldTextClass("esiApplicable", filters.esiApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("esiApplicable", filters.esiApplicable)}`}>
                  ESI Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.ceilingPf}
                  onChange={handleChange("ceilingPf")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("ceilingPf", filters.ceilingPf)} ${getFieldTextClass("ceilingPf", filters.ceilingPf)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("ceilingPf", filters.ceilingPf)}`}>
                  Ceiling PF
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.hasUAN}
                  onChange={handleChange("hasUAN")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("hasUAN", filters.hasUAN)} ${getFieldTextClass("hasUAN", filters.hasUAN)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("hasUAN", filters.hasUAN)}`}>
                  Has UAN
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.hasESIIP}
                  onChange={handleChange("hasESIIP")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("hasESIIP", filters.hasESIIP)} ${getFieldTextClass("hasESIIP", filters.hasESIIP)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("hasESIIP", filters.hasESIIP)}`}>
                  Has ESI IP
                </label>
              </div>
            </div>
          </div>


          {/* PT and Gratuity applicability */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative">
                <select
                  value={filters.ptApplicable}
                  onChange={handleChange("ptApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("ptApplicable", filters.ptApplicable)} ${getFieldTextClass("ptApplicable", filters.ptApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("ptApplicable", filters.ptApplicable)}`}>
                  PT Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.gratuityApplicable}
                  onChange={handleChange("gratuityApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-none border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("gratuityApplicable", filters.gratuityApplicable)} ${getFieldTextClass("gratuityApplicable", filters.gratuityApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("gratuityApplicable", filters.gratuityApplicable)}`}>
                  Gratuity Applicable
                </label>
              </div>
            </div>
          </div>


          {/* CTC Range */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              CTC Range
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                  } ${getFieldTextClass("ctcFrom", filters.ctcFrom)}`}
                >
                  CTC From (₹)
                </label>
                <input
                  type="number"
                  value={filters.ctcFrom || ""}
                  onChange={handleChange("ctcFrom")}
                  onBlur={() => handleFieldBlur("ctcFrom")}
                  placeholder="Enter Minimum CTC"
                  className={`block w-full px-3 py-2 text-sm rounded-none border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${
                    formErrors.ctcFrom || formErrors.ctcError
                      ? "border-red-500"
                      : getFieldBorderClass("ctcFrom", filters.ctcFrom)
                  } ${getFieldTextClass("ctcFrom", filters.ctcFrom)}`}
                  min="0"
                  step="0.01"
                />
                {formErrors.ctcFrom && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.ctcFrom}
                  </div>
                )}
              </div>


              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                  } ${getFieldTextClass("ctcTo", filters.ctcTo)}`}
                >
                  CTC To (₹)
                </label>
                <input
                  type="number"
                  value={filters.ctcTo || ""}
                  onChange={handleChange("ctcTo")}
                  onBlur={() => handleFieldBlur("ctcTo")}
                  placeholder="Enter Maximum CTC"
                  className={`block w-full px-3 py-2 text-sm rounded-none border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${
                    formErrors.ctcTo || formErrors.ctcError
                      ? "border-red-500"
                      : getFieldBorderClass("ctcTo", filters.ctcTo)
                  } ${getFieldTextClass("ctcTo", filters.ctcTo)}`}
                  min="0"
                  step="0.01"
                />
                {formErrors.ctcTo && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.ctcTo}
                  </div>
                )}
              </div>
            </div>
            {formErrors.ctcError &&
              !formErrors.ctcFrom &&
              !formErrors.ctcTo && (
                <div className="text-sm text-red-500 mt-2">
                  {formErrors.ctcError}
                </div>
              )}
          </div>
        </fieldset>


        {/* Fixed Action Buttons */}
        <div className="flex justify-between items-center p-6 border-t flex-shrink-0">
          <div className="min-w-0 pr-4">
            {(() => {
              const selected = getSelectedSummary();
              if (!selected.length) return null;
              return (
                <div className={`text-sm ${darkMode ? "text-green-300" : "text-green-700"} truncate`}>
                  <span className={darkMode ? "text-[#A6A9C8]" : "text-gray-600"}>
                    Selected: 
                  </span>
                  {selected.map((s, idx) => (
                    <span key={`${s.label}-${idx}`}>
                      {idx > 0 ? ", " : ""}
                      {s.label}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClear}
              className={`${
                darkMode
                  ? "bg-[#4C4C4C] text-[#E0E0E0]"
                  : "bg-gray-200 text-gray-800"
              } px-6 py-3 rounded-none font-medium`}
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              disabled={hasErrors}
              className={`${
                darkMode ? "bg-[#BB86FC] text-white" : "bg-blue-600 text-white"
              } ${
                hasErrors ? "opacity-50 cursor-not-allowed" : ""
              } px-6 py-3 rounded-none font-medium`}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


